import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import T from '../styles/theme';
import { Avatar, Badge, Btn, Select, Checkbox } from '../components/UI';
import Icon from '../components/Icon';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';
import supabase from '../data/supabase';
import apiFetch from '../data/api';
import CoachingPopup from '../components/CoachingPopup';

// ─── HELPERS ───────────────────────────────────────
function fmt(s) {
  if (!s || s === 0) return "0:00";
  return String(Math.floor(s / 60)).padStart(2, "0") + ":" + String(s % 60).padStart(2, "0");
}

function formatPhone(ph) {
  if (!ph) return "—";
  const digits = ph.replace(/[^\d]/g, "");
  if (digits.length === 11 && digits[0] === "1") return `+1 (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  return ph;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const mo = String(d.getMonth()+1).padStart(2,"0");
  const dy = String(d.getDate()).padStart(2,"0");
  const yr = String(d.getFullYear()).slice(-2);
  let h = d.getHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
  const mn = String(d.getMinutes()).padStart(2,"0");
  return mo + "/" + dy + "/" + yr + " " + h + ":" + mn + " " + ap;
}

const statusConfig = {
  completed: { label: "Connected", color: "#FAFAFA" },
  no_answer: { label: "No Answer", color: "#FAFAFA" },
  voicemail: { label: "Voicemail", color: "#FAFAFA" },
  busy: { label: "Busy", color: "#FAFAFA" },
  missed: { label: "Missed", color: "#FAFAFA" },
};

// ─── CALL LOG FILTER DEFINITIONS ──────────────────
const FILTER_FIELDS = [
  { id: "status", label: "Call Status", type: "select", options: ["completed", "no_answer", "voicemail", "busy", "missed"] },
  { id: "direction", label: "Direction", type: "select", options: ["outbound", "inbound"] },
  { id: "assigned", label: "Assigned To", type: "owner" },
  { id: "has_recording", label: "Has Recording", type: "boolean" },
  { id: "min_duration", label: "Min Duration (sec)", type: "number" },
  { id: "max_duration", label: "Max Duration (sec)", type: "number" },
  { id: "date_after", label: "Date After", type: "date" },
  { id: "date_before", label: "Date Before", type: "date" },
  { id: "contact_name", label: "Contact Name", type: "text" },
  { id: "company", label: "Company", type: "text" },
  { id: "phone", label: "Phone Number", type: "text" },
];

function applyCallFilter(log, f) {
  const { field, value } = f;
  if (!value && value !== false && value !== "false") return true;
  const v = String(value).toLowerCase();

  switch (field) {
    case "status": return (log.status || "").toLowerCase() === v;
    case "direction": return (log.direction || "outbound").toLowerCase() === v;
    case "assigned": return (log.user_id || "") === value;
    case "has_recording": return value === "true" ? !!log.recording_url : !log.recording_url;
    case "min_duration": return (log.duration || 0) >= Number(value);
    case "max_duration": return (log.duration || 0) <= Number(value);
    case "date_after": return (log.started_at || "") >= value;
    case "date_before": return (log.started_at || "") <= value;
    case "contact_name": {
      const ct = log._contact;
      if (!ct) return false;
      return ((ct.fn || "") + " " + (ct.ln || "")).toLowerCase().includes(v);
    }
    case "company": {
      const ct = log._contact;
      return (ct?.co || ct?.company || "").toLowerCase().includes(v);
    }
    case "phone": return (log._phone || "").includes(v);
    default: return true;
  }
}

function applyAllCallFilters(logs, filters) {
  if (!filters || filters.length === 0) return logs;
  return logs.filter(l => filters.every(f => applyCallFilter(l, f)));
}

// Map UI sort key → server-side .order(...) config.
// Foreign-table sorts use PostgREST embedded resource ordering.
// Direct columns omit foreignTable. The caller adds a started_at desc
// tiebreaker on top of any non-default sort to keep pagination stable
// when two rows have tied sort values (very common with NULL durations
// or duplicate contact names).
function mapSortToOrderConfig(sortCol, sortDir) {
  const ascending = sortDir === "asc";
  switch (sortCol) {
    case "contact":     return { column: "first_name", foreignTable: "contacts", ascending };
    case "disposition": return { column: "label", foreignTable: "call_dispositions", ascending };
    case "user":
    case "assigned":    return { column: "first_name", foreignTable: "users", ascending };
    case "duration":    return { column: "duration", ascending };
    case "status":      return { column: "status", ascending };
    case "started_at":
    default:            return { column: "started_at", ascending };
  }
}

// ─── DEFAULT VIEWS ────────────────────────────────
const DEFAULT_VIEWS = [
  { id: "all", name: "All Calls", filters: [], system: true },
  { id: "connected", name: "Connected", filters: [{ field: "status", value: "completed" }], system: true },
  { id: "missed", name: "No Answer / Missed", filters: [{ field: "status", value: "no_answer" }], system: true },
  { id: "voicemail", name: "Voicemail", filters: [{ field: "status", value: "voicemail" }], system: true },
  { id: "has_recording", name: "With Recordings", filters: [{ field: "has_recording", value: "true" }], system: true },
  { id: "long_calls", name: "Long Calls (2m+)", filters: [{ field: "min_duration", value: "120" }], system: true },
];

// ─── FILTER ROW COMPONENT ─────────────────────────
function CallFilterRow({ filter, onChange, onRemove, teamMembers }) {
  const fieldDef = FILTER_FIELDS.find(f => f.id === filter.field) || {};
  const selStyle = { width: 200, borderRadius: 8 };
  const inputStyle = { padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 12, outline: "none", fontFamily: "inherit", width: 200 };

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
      <Select value={filter.field} onChange={e => onChange({ ...filter, field: e.target.value, value: "" })} style={selStyle}>
        <option value="">Select field...</option>
        {FILTER_FIELDS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
      </Select>

      {fieldDef.type === "select" && (
        <Select value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })} style={selStyle}>
          <option value="">Any</option>
          {fieldDef.options.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1).replace("_", " ")}</option>)}
        </Select>
      )}
      {fieldDef.type === "owner" && (
        <Select value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })} style={selStyle}>
          <option value="">Any rep</option>
          {(teamMembers || []).map(m => <option key={m._dbId || m.id} value={m._dbId || m.id}>{m.name}</option>)}
        </Select>
      )}
      {fieldDef.type === "text" && (
        <input value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })} placeholder="Contains..." style={inputStyle} />
      )}
      {fieldDef.type === "number" && (
        <input type="number" value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })} placeholder="Value" style={inputStyle} />
      )}
      {fieldDef.type === "date" && (
        <input type="date" value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })} style={inputStyle} />
      )}
      {fieldDef.type === "boolean" && (
        <Select value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })} style={selStyle}>
          <option value="">—</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </Select>
      )}

      <button onClick={onRemove} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", color: T.tx3, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>×</button>
    </div>
  );
}

// ─── RECORDING PLAY BUTTON ────────────────────────
function RecPlayBtn({ url }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const recSid = (url.match(/Recordings\/(RE[a-f0-9]+)/i) || [])[1];
  const proxyUrl = recSid ? `/api/twilio/play?sid=${recSid}` : url;

  useEffect(() => {
    const audio = new Audio(proxyUrl);
    audioRef.current = audio;
    audio.addEventListener("loadedmetadata", () => setDuration(audio.duration));
    audio.addEventListener("timeupdate", () => setProgress(audio.currentTime));
    audio.addEventListener("ended", () => { setPlaying(false); setProgress(0); });
    return () => { audio.pause(); audio.src = ""; };
  }, [proxyUrl]);

  const toggle = (e) => {
    e.stopPropagation();
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  const pct = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={e => e.stopPropagation()}>
      <button onClick={toggle} style={{
        width: 24, height: 24, borderRadius: 8, border: "none", cursor: "pointer",
        background: playing ? T.red : "#4059AD", color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 9, flexShrink: 0, padding: 0,
      }}>{playing ? "⏸" : "▶"}</button>
      {playing && <div style={{ width: 40, height: 3, background: "rgba(217,219,221,0.12)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ width: pct + "%", height: "100%", background: "#4059AD", borderRadius: 2, transition: "width 0.2s" }} />
      </div>}
    </div>
  );
}


// ═══════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════
export default function CallLogsPage({ isMobile }) {
  const store = useStore();
  const { contacts, teamMembers, savedViews } = store;
  const auth = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortCol, setSortCol] = useState("started_at");
  const [sortDir, setSortDir] = useState("desc");
  // Server-supplied total for the current query — drives the "of N" footer
  // and totalPages. Replaces filtered.length under client-side pagination.
  const [totalCount, setTotalCount] = useState(0);
  // Stats are org-wide in commit 1 (independent of pagination + filters).
  // statsScope is scaffolded for the commit-3 toggle: "org" shows org-wide
  // totals (current behavior); "filter" will rerun with the active filter
  // clauses applied. The toggle UI doesn't ship until commit 3.
  const [statsScope, setStatsScope] = useState("org"); // "org" | "filter"
  const [statsTotal, setStatsTotal] = useState(0);
  const [statsConnected, setStatsConnected] = useState(0);
  const [statsTotalDuration, setStatsTotalDuration] = useState(0);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [analysisCache, setAnalysisCache] = useState({});
  const [coachingPopup, setCoachingPopup] = useState(null); // { analysisId, contactName, date, duration }
  const analysisPollRef = React.useRef(null);

  // Poll for analysis status when a row is expanded and analysis is in-progress
  useEffect(() => {
    if (analysisPollRef.current) { clearInterval(analysisPollRef.current); analysisPollRef.current = null; }
    if (!expandedLogId) return;
    const cached = analysisCache[expandedLogId];
    if (!cached || cached === "none" || cached.status === "completed" || cached.status === "failed") return;

    // Poll every 5 seconds
    analysisPollRef.current = setInterval(async () => {
      try {
        const r = await apiFetch("/api/ai/notetaker", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "get_analysis", callLogId: expandedLogId }) });
        const d = await r.json();
        if (d.analysis) {
          setAnalysisCache(p => ({ ...p, [expandedLogId]: d.analysis }));
          if (d.analysis.status === "completed" || d.analysis.status === "failed") {
            clearInterval(analysisPollRef.current);
            analysisPollRef.current = null;
          }
        }
      } catch (e) { /* silent — will retry next interval */ }
    }, 5000);

    return () => { if (analysisPollRef.current) { clearInterval(analysisPollRef.current); analysisPollRef.current = null; } };
  }, [expandedLogId, analysisCache[expandedLogId]?.status]);

  // ── View system (mirrors People page) ──
  const [activeViewId, setActiveViewIdRaw] = useState(() => {
    try { return localStorage.getItem("cf_calllogs_activeView") || "all"; } catch { return "all"; }
  });
  const setActiveViewId = (id) => { setActiveViewIdRaw(id); try { localStorage.setItem("cf_calllogs_activeView", id); } catch {} };
  const [filters, setFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showSaveView, setShowSaveView] = useState(false);
  const [shareView, setShareView] = useState(false);
  const [saveViewError, setSaveViewError] = useState("");
  const [newViewName, setNewViewName] = useState("");
  const [showViewPicker, setShowViewPicker] = useState(false);
  const [viewSearch, setViewSearch] = useState("");
  const viewPickerRef = useRef(null);

  // ── Column editor ──
  const ALL_COLUMNS = useMemo(() => [
    { id: "contact", label: "Contact", required: true },
    { id: "phone", label: "Phone Number" },
    { id: "assigned", label: "Assigned To" },
    { id: "status", label: "Status" },
    { id: "disposition", label: "Disposition" },
    { id: "duration", label: "Duration" },
    { id: "date", label: "Date / Time" },
    { id: "direction", label: "Direction" },
    { id: "recording", label: "Recording" },
  ], []);
  const DEFAULT_COLS = ["contact", "phone", "assigned", "status", "disposition", "duration", "date", "direction", "recording"];
  const [visibleCols, setVisibleCols] = useState(DEFAULT_COLS);
  const [showColEditor, setShowColEditor] = useState(false);
  const [colEditorDraft, setColEditorDraft] = useState(DEFAULT_COLS);
  const [colSearch, setColSearch] = useState("");
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  // ── Combine default + saved call views ──
  const callSavedViews = useMemo(() =>
    (savedViews || []).filter(v => v.entity === "calls"),
    [savedViews]
  );
  const views = useMemo(() => [...DEFAULT_VIEWS, ...callSavedViews], [callSavedViews]);

  // Restore persisted view filters on mount
  const [hasRestoredView, setHasRestoredView] = useState(false);
  useEffect(() => {
    if (hasRestoredView) return;
    if (activeViewId === "all") { setHasRestoredView(true); return; }
    const allViews = [...DEFAULT_VIEWS, ...callSavedViews];
    const view = allViews.find(v => v.id === activeViewId);
    if (view) {
      setFilters(view.filters ? view.filters.map(f => ({ ...f })) : []);
      setHasRestoredView(true);
    } else if (callSavedViews.length > 0 || !loading) {
      setActiveViewId("all");
      setHasRestoredView(true);
    }
  }, [activeViewId, callSavedViews, loading, hasRestoredView]);

  // Close dropdown on outside click
  useEffect(() => {
    const h = (e) => { if (viewPickerRef.current && !viewPickerRef.current.contains(e.target)) setShowViewPicker(false); };
    if (showViewPicker) setTimeout(() => document.addEventListener("click", h), 0);
    return () => document.removeEventListener("click", h);
  }, [showViewPicker]);

  // Switch view
  const switchView = useCallback((viewId) => {
    setActiveViewId(viewId);
    const view = [...DEFAULT_VIEWS, ...callSavedViews].find(v => v.id === viewId) || DEFAULT_VIEWS[0];
    setFilters(view.filters ? view.filters.map(f => ({ ...f })) : []);
    setSearchTerm("");
  }, [callSavedViews]);

  // Save custom view to Supabase (entity: "calls")
  const saveViewHandler = async () => {
    if (!newViewName.trim()) return;
    const name = newViewName.trim();
    const duplicate = views.find(v => v.name.toLowerCase() === name.toLowerCase());
    if (duplicate) { setSaveViewError("A view named \"" + name + "\" already exists"); return; }
    setSaveViewError("");
    const viewData = { name, filters: filters.map(f => ({ ...f })), icon: null, isShared: shareView, entity: "calls" };
    if (shareView) viewData.columns = [...visibleCols];
    await store.saveView(viewData);
    setNewViewName("");
    setShareView(false);
    setShowSaveView(false);
    setShowFilters(false);
  };

  const deleteViewHandler = (viewId) => {
    store.deleteView(viewId);
    if (activeViewId === viewId) switchView("all");
  };

  // ── Load the current page of call logs ──
  // Single useEffect driven by [orgId, currentPage, pageSize, sortCol, sortDir].
  // Returns the page rows AND the server-side total count alongside (PostgREST
  // Content-Range header via { count: "exact" }), so the pager footer reads
  // "X-Y of [actual total]" instead of the legacy 500-row cap.
  // Joins contacts/call_dispositions/users via foreign-table embed — same
  // pattern already used in callLogger.getCallHistory and auth.js. The cellMap
  // reads log.contacts.first_name etc. directly; no in-memory enrichment needed.
  useEffect(() => {
    async function fetchPage() {
      const orgId = auth.currentUser?._orgId;
      if (!orgId) return;
      const start = (currentPage - 1) * pageSize;
      const end = start + pageSize - 1;
      const orderConfig = mapSortToOrderConfig(sortCol, sortDir);

      let query = supabase
        .from("call_logs")
        .select(
          "*, contacts(id, first_name, last_name, company, phone), call_dispositions(label, behavior), users(first_name, last_name)",
          { count: "exact" }
        )
        .eq("org_id", orgId);

      // Primary sort.
      if (orderConfig.foreignTable) {
        query = query.order(orderConfig.column, {
          foreignTable: orderConfig.foreignTable,
          ascending: orderConfig.ascending,
        });
      } else {
        query = query.order(orderConfig.column, { ascending: orderConfig.ascending });
      }
      // Tiebreaker: started_at desc keeps pagination stable when sorted
      // values are tied. Skip if the primary sort already IS started_at.
      if (orderConfig.column !== "started_at") {
        query = query.order("started_at", { ascending: false });
      }
      query = query.range(start, end);

      try {
        const { data, error, count } = await query;
        if (error) {
          console.error("[CoreFlow] Call logs fetch failed:", error.message);
          return;
        }
        setLogs(data || []);
        setTotalCount(count || 0);
      } catch (e) {
        console.warn("[CoreFlow] Call logs load failed:", e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchPage();
  }, [auth.currentUser?._orgId, currentPage, pageSize, sortCol, sortDir]);

  // ── Org-wide stats fetch (commit 1: not filter-aware) ──
  // Three queries: total count, connected count (status=completed AND
  // duration>0), and a duration-sum derived by summing every row's duration
  // client-side. The third is wasteful at scale; commit 3 replaces it with
  // a Postgres RPC that does SUM() server-side. Acceptable for now since
  // current orgs have <10k call_logs rows.
  // Runs once per org, independent of pagination/sort/filters. statsScope
  // hook stays "org" until the commit-3 toggle wires it to "filter".
  useEffect(() => {
    async function fetchStats() {
      const orgId = auth.currentUser?._orgId;
      if (!orgId) return;
      try {
        const [totalRes, connectedRes, durationRes] = await Promise.all([
          supabase.from("call_logs").select("*", { count: "exact", head: true }).eq("org_id", orgId),
          supabase.from("call_logs").select("*", { count: "exact", head: true }).eq("org_id", orgId).eq("status", "completed").gt("duration", 0),
          supabase.from("call_logs").select("duration").eq("org_id", orgId),
        ]);
        setStatsTotal(totalRes.count || 0);
        setStatsConnected(connectedRes.count || 0);
        const sum = (durationRes.data || []).reduce((acc, r) => acc + (r.duration || 0), 0);
        setStatsTotalDuration(sum);
      } catch (e) {
        console.warn("[CoreFlow] Call logs stats failed:", e.message);
      }
    }
    fetchStats();
  }, [auth.currentUser?._orgId]);

  // ── Filtered/paginated rows ──
  // Server-side pagination: `logs` is already the current page from the
  // query above, sorted server-side, with contacts/dispositions/users
  // joined in. Keep `filtered` as a passthrough alias so the existing
  // render touchpoints (rows, selection, footer ranges) keep working
  // unchanged while commit 2 reintroduces filter/search server-side.
  const filtered = logs;

  // Pagination — totalPages now comes from the server-supplied count.
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Sort change resets to page 1 — server-side pagination would otherwise
  // request a page index that no longer makes sense for the new ordering.
  // (pageSize change already does this; filter/search change will too in
  // commit 2.)
  const handleSort = (col) => {
    setCurrentPage(1);
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return null;
    return <span style={{ color: "#FAFAFA", fontSize: 11, marginLeft: 4 }}>{sortDir === "asc" ? "▲" : "▼"}</span>;
  };

  // ── Stats ──
  // Org-wide totals from the dedicated stats useEffect above. Filter-aware
  // mode + scope toggle ship in commit 3.
  const connected = statsConnected;
  const totalDuration = statsTotalDuration;
  const avgDuration = statsConnected > 0 ? Math.round(statsTotalDuration / statsConnected) : 0;
  const connectRate = statsTotal > 0 ? Math.round((statsConnected / statsTotal) * 100) : 0;

  const activeView = views.find(v => v.id === activeViewId) || views[0];

  // Commit 1 only: filter UI + search are hidden, so saved views with
  // non-empty filter arrays would silently constrain results with no way
  // for the user to clear them. Force the active view back to "All Calls"
  // on mount and clear the filter array. Commit 2 removes this guard
  // alongside re-enabling filter UI.
  useEffect(() => {
    if (activeViewId !== "all") {
      setActiveViewId("all");
      setFilters([]);
    }
    // eslint-disable-next-line
  }, []);

  // ── Selection ──
  const allSelected = filtered.length > 0 && filtered.every(l => selected.has(l.id));
  const toggleAll = () => { if (allSelected) setSelected(new Set()); else setSelected(new Set(filtered.map(l => l.id))); };
  const toggleOne = (id) => { setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };

  // Save columns to Supabase
  const applyColumns = (cols) => {
    setVisibleCols(cols);
    try { localStorage.setItem("cf_call_columns", JSON.stringify(cols)); } catch {}
    if (store.currentUserId) {
      supabase.from("user_preferences").upsert({
        user_id: store.currentUserId,
        org_id: store.currentOrgId,
        key: "call_columns",
        value: cols,
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id,key" }).then(({ error }) => {
        if (error) console.warn("[CoreFlow] Column save error:", error.message);
      });
    }
  };

  // Load column config on mount
  useEffect(() => {
    if (!store.currentUserId) return;
    supabase.from("user_preferences").select("value")
      .eq("user_id", store.currentUserId).eq("key", "call_columns").maybeSingle()
      .then(({ data }) => {
        if (data?.value && Array.isArray(data.value)) {
          setVisibleCols(data.value);
          setColEditorDraft(data.value);
          localStorage.setItem("cf_call_columns", JSON.stringify(data.value));
        }
      });
  }, [store.currentUserId]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: T.tx3, fontSize: 13 }}>Loading call logs...</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "transparent", fontFamily: "inherit" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* ═══ VIEW DROPDOWN BAR ═══ */}
      <div style={{ padding: "14px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div ref={viewPickerRef} style={{ position: "relative" }}>
            <button onClick={() => { setShowViewPicker(!showViewPicker); setViewSearch(""); }}
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: "6px 12px",
                borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
                background: showViewPicker ? "rgba(255,255,255,0.08)" : "transparent",
                cursor: "pointer", fontFamily: "inherit", transition: "all .15s ease",
              }}
              onMouseEnter={e => { if (!showViewPicker) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
              onMouseLeave={e => { if (!showViewPicker) e.currentTarget.style.background = "transparent"; }}>
              <Icon name="phone" size={14} color="#FAFAFA" />
              <span style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA" }}>{activeView.name}</span>
              <span style={{ fontSize: 10, color: "#FAFAFA", opacity: 0.5, marginLeft: 2 }}>▾</span>
            </button>

            {/* ── View Picker Dropdown ── */}
            {showViewPicker && (
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", left: 0, width: 300,
                background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)",
                borderRadius: 8, boxShadow: "0 24px 64px rgba(0,0,0,0.80)",
                zIndex: 99999, overflow: "hidden",
              }}>
                <div style={{ padding: "12px 14px 10px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
                  <input autoFocus value={viewSearch} onChange={e => setViewSearch(e.target.value)}
                    placeholder="Search views..."
                    style={{
                      width: "100%", padding: "9px 12px", borderRadius: 8,
                      border: "1px solid rgba(217,219,221,0.10)", background: "rgba(255,255,255,0.04)",
                      color: "#FAFAFA", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box",
                    }} />
                </div>
                <div style={{ maxHeight: 320, overflowY: "auto", padding: "8px 10px" }}>
                  {views.filter(v => !viewSearch || v.name.toLowerCase().includes(viewSearch.toLowerCase())).map(v => {
                    const canDelete = !v.system && !DEFAULT_VIEWS.find(d => d.id === v.id);
                    return (
                      <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                        <div onClick={() => { switchView(v.id); setShowViewPicker(false); }}
                          style={{
                            display: "flex", alignItems: "center", flex: 1, padding: "11px 14px",
                            borderRadius: 8, cursor: "pointer",
                            background: activeViewId === v.id ? "#4059AD" : "rgba(255,255,255,0.04)",
                            color: activeViewId === v.id ? "#fff" : "#FAFAFA",
                          }}
                          onMouseEnter={e => { if (activeViewId !== v.id) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                          onMouseLeave={e => { if (activeViewId !== v.id) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                          <span style={{ fontSize: 13, fontWeight: activeViewId === v.id ? 600 : 400 }}>{v.name}</span>
                        </div>
                        {canDelete ? (
                          <div onClick={() => deleteViewHandler(v.id)}
                            style={{
                              width: 26, height: 26, borderRadius: 7, background: "rgba(255,255,255,0.04)",
                              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 11, color: "#FAFAFA", opacity: 0.4, flexShrink: 0,
                            }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = "#EF4444"; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = "0.4"; e.currentTarget.style.color = "#FAFAFA"; }}>×</div>
                        ) : <div style={{ width: 26, flexShrink: 0 }} />}
                      </div>
                    );
                  })}
                  <div style={{ borderTop: "1px solid rgba(217,219,221,0.08)", marginTop: 6, paddingTop: 6 }}>
                    <div onClick={() => {
                      setFilters([{ field: "", value: "" }]);
                      setShowFilters(true);
                      setActiveViewId("custom");
                      setShowViewPicker(false);
                    }}
                      style={{ padding: "10px 14px", borderRadius: 8, cursor: "pointer", color: "#4059AD", fontSize: 13, fontWeight: 400 }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      + New View
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Total count — server-side, reflects the entire filter result, not the page */}
          <span style={{ fontSize: 12, color: "#FAFAFA", opacity: 0.5 }}>{totalCount.toLocaleString()} calls</span>

          {/* Filter pills hidden in commit 1 — restored in commit 2 alongside server-side filter wiring. */}
          {false && filters.length > 0 && <>
            <button onClick={() => setShowFilters(true)}
              style={{ padding: "5px 14px", borderRadius: 8, fontSize: 10, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>
              Edit
            </button>
            <button onClick={() => { setFilters([]); setShowFilters(false); switchView("all"); }}
              style={{ padding: "5px 14px", borderRadius: 8, fontSize: 10, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>
              Clear All
            </button>
          </>}
        </div>

        {/* Stats pills — right side */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ padding: "4px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.03)", display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA" }}>{connected.toLocaleString()}</span>
            <span style={{ fontSize: 8, fontWeight: 300, color: T.tx3, letterSpacing: ".04em", textTransform: "uppercase" }}>Connected</span>
          </div>
          <div style={{ padding: "4px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.03)", display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA" }}>{connectRate}%</span>
            <span style={{ fontSize: 8, fontWeight: 300, color: T.tx3, letterSpacing: ".04em", textTransform: "uppercase" }}>Rate</span>
          </div>
          <div style={{ padding: "4px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.03)", display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA" }}>{fmt(avgDuration)}</span>
            <span style={{ fontSize: 8, fontWeight: 300, color: T.tx3, letterSpacing: ".04em", textTransform: "uppercase" }}>Avg</span>
          </div>
          <div style={{ padding: "4px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.03)", display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA" }}>{fmt(totalDuration)}</span>
            <span style={{ fontSize: 8, fontWeight: 300, color: T.tx3, letterSpacing: ".04em", textTransform: "uppercase" }}>Total</span>
          </div>
          {/* Scope indicator — turns into an interactive toggle in commit 3 */}
          <span style={{ fontSize: 9, color: T.tx3, marginLeft: 4 }}>
            Stats: {statsScope === "filter" ? "Filter" : "All org"}
          </span>
        </div>
      </div>

      {/* ═══ HEADER BAR — COLUMNS only (search hidden in commit 1) ═══ */}
      <div style={{ display: "flex", gap: 6, padding: "10px 24px 14px", alignItems: "center" }}>
        {/* Search input hidden in commit 1 — server-side .or() across contacts.first_name/last_name/phone returns in commit 2 with debounce. */}
        {false && (
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search..."
            style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(0,0,0,0.15)", fontSize: 12, fontFamily: "inherit", outline: "none", color: "#FAFAFA", width: 180 }} />
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => { setColEditorDraft([...visibleCols]); setColSearch(""); setShowColEditor(true); }}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>
          <Icon name="settings" size={12} color="#FAFAFA" /> Columns
        </button>
      </div>

      {/* ═══ FILTER MODAL ═══ */}
      {showFilters && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setShowFilters(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)" }} />
          <div style={{ position: "relative", width: 520, maxWidth: "92vw", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 40px 100px rgba(0,0,0,0.40)", padding: "20px 24px" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Call Filters</span>
              <button onClick={() => setShowFilters(false)} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: T.tx3 }}>×</button>
            </div>

            {/* Filter rows */}
            {filters.map((f, i) => (
              <CallFilterRow key={i} filter={f} teamMembers={store.teamMembers}
                onChange={(updated) => setFilters(prev => prev.map((x, j) => j === i ? updated : x))}
                onRemove={() => setFilters(prev => prev.filter((_, j) => j !== i))} />
            ))}

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(217,219,221,0.08)" }}>
              <button onClick={() => setFilters(prev => [...prev, { field: "", value: "" }])}
                style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: "#D9DBDD", background: T.blue, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                + Add Filter
              </button>
              <div style={{ display: "flex", gap: 6 }}>
                {filters.some(f => f.field) && (
                  <button onClick={() => { setSaveViewError(""); setShowSaveView(true); }}
                    style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: "#D9DBDD", background: T.blue, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                    Save as View
                  </button>
                )}
                <button onClick={() => setShowFilters(false)}
                  style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>
                  Done
                </button>
              </div>
            </div>

            {/* ── Save as View nested popup ── */}
            {showSaveView && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, borderRadius: 8, overflow: "hidden" }}>
                <div onClick={() => setShowSaveView(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.60)" }} />
                <div style={{ position: "relative", width: 340, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: "20px 24px", boxShadow: "0 20px 60px rgba(0,0,0,0.40)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Save as View</span>
                    <button onClick={() => setShowSaveView(false)} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: T.tx3 }}>×</button>
                  </div>
                  <input value={newViewName} onChange={e => { setNewViewName(e.target.value); setSaveViewError(""); }}
                    placeholder="View name..." onKeyDown={e => e.key === "Enter" && saveViewHandler()} autoFocus
                    style={{
                      width: "100%", padding: "10px 14px", borderRadius: 8,
                      border: "1px solid " + (saveViewError ? "rgba(239,68,68,0.5)" : "rgba(217,219,221,0.12)"),
                      background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 13,
                      outline: "none", fontFamily: "inherit", marginBottom: saveViewError ? 6 : 12, boxSizing: "border-box",
                    }} />
                  {saveViewError && <div style={{ fontSize: 11, color: T.red, marginBottom: 10 }}>{saveViewError}</div>}
                  <div onClick={() => setShareView(!shareView)}
                    style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 14, padding: "8px 0" }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: 8,
                      background: shareView ? T.blue : "transparent",
                      border: shareView ? "none" : "2px solid rgba(217,219,221,0.25)",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s",
                    }}>
                      {shareView && <span style={{ color: "#fff", fontSize: 11, fontWeight: 400 }}>✓</span>}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>Share with team</div>
                      <div style={{ fontSize: 10, color: T.tx3 }}>Everyone in your org will see this view</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <button onClick={() => setShowSaveView(false)}
                      style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                    <button onClick={saveViewHandler}
                      style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: "#D9DBDD", background: T.blue, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>, document.body
      )}

      {/* ═══ COLUMN EDITOR MODAL ═══ */}
      {showColEditor && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setShowColEditor(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)" }} />
          <div style={{ position: "relative", width: 700, maxWidth: "94vw", height: 520, maxHeight: "80vh", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 40px 100px rgba(0,0,0,0.40)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid rgba(217,219,221,0.08)", flexShrink: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA" }}>Edit Columns</span>
              <button onClick={() => setShowColEditor(false)} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "#FAFAFA" }}>×</button>
            </div>
            <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
              {/* Left: Available */}
              <div style={{ width: "50%", borderRight: "1px solid rgba(217,219,221,0.08)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "12px 16px 8px", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 400, color: "#FAFAFA", letterSpacing: ".06em", marginBottom: 8, opacity: 0.5 }}>AVAILABLE FIELDS</div>
                  <input value={colSearch} onChange={e => setColSearch(e.target.value)} placeholder="Search fields..."
                    style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: "#FAFAFA", fontSize: 11, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "4px 16px 12px" }}>
                  {ALL_COLUMNS.filter(c => !c.required).filter(c => !colSearch || c.label.toLowerCase().includes(colSearch.toLowerCase())).map(col => {
                    const isActive = colEditorDraft.includes(col.id);
                    return (
                      <div key={col.id} onClick={() => { if (isActive) setColEditorDraft(prev => prev.filter(c => c !== col.id)); else setColEditorDraft(prev => [...prev, col.id]); }}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", marginBottom: 2, borderRadius: 8, cursor: "pointer", transition: "background 0.1s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ width: 20, height: 20, borderRadius: 8, background: isActive ? "#4059AD" : "transparent", border: isActive ? "1.5px solid #4059AD" : "1.5px solid rgba(217,219,221,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {isActive && <span style={{ color: "#fff", fontSize: 11, fontWeight: 400 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 12, color: "#FAFAFA" }}>{col.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Right: Selected */}
              <div style={{ width: "50%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "12px 16px 8px", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 400, color: "#FAFAFA", letterSpacing: ".06em", opacity: 0.5 }}>SELECTED COLUMNS ({colEditorDraft.length})</div>
                  <div style={{ fontSize: 9, color: "#FAFAFA", marginTop: 2, opacity: 0.3 }}>Drag to reorder</div>
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "4px 16px 12px" }}>
                  {colEditorDraft.map((colId, idx) => {
                    const col = ALL_COLUMNS.find(c => c.id === colId);
                    if (!col) return null;
                    return (
                      <div key={colId} draggable
                        onDragStart={() => { dragItem.current = idx; }}
                        onDragEnter={() => { dragOver.current = idx; }}
                        onDragOver={e => e.preventDefault()}
                        onDragEnd={() => {
                          const f = dragItem.current, t = dragOver.current;
                          if (f === null || t === null || f === t) return;
                          const n = [...colEditorDraft]; const item = n.splice(f, 1)[0]; n.splice(t, 0, item);
                          setColEditorDraft(n); dragItem.current = null; dragOver.current = null;
                        }}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", marginBottom: 3, borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(217,219,221,0.08)", cursor: "grab", userSelect: "none" }}>
                        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", cursor: "grab", lineHeight: 1 }}>⠿</span>
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 400, color: "#FAFAFA" }}>{col.label}</span>
                        {!col.required && <button onClick={e => { e.stopPropagation(); setColEditorDraft(prev => prev.filter(c => c !== colId)); }}
                          style={{ width: 22, height: 22, borderRadius: 6, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#FAFAFA" }}>×</button>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", padding: "12px 20px", borderTop: "1px solid rgba(217,219,221,0.08)", flexShrink: 0 }}>
              <button onClick={() => setColEditorDraft([...DEFAULT_COLS])} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, color: "#FAFAFA", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>Reset</button>
              <button onClick={() => setShowColEditor(false)} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, color: "#FAFAFA", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={() => { applyColumns([...colEditorDraft]); setShowColEditor(false); setColSearch(""); }}
                style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, color: "#fff", background: "#4059AD", border: "none", cursor: "pointer", fontFamily: "inherit" }}>Apply</button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* ═══ TABLE ═══ */}
      <div style={{ padding: isMobile ? "0 8px" : "0 24px", flex: 1, overflowY: "auto", overflowX: isMobile ? "auto" : "hidden" }}>
        {(() => {
          const colDefs = {
            contact: { label: "CONTACT", col: "contact", w: "1.4fr" },
            phone: { label: "PHONE NUMBER", col: null, w: "150px" },
            assigned: { label: "ASSIGNED TO", col: "user", w: "130px" },
            status: { label: "STATUS", col: "status", w: "95px" },
            disposition: { label: "DISPOSITION", col: "disposition", w: "130px" },
            duration: { label: "DURATION", col: "duration", w: "80px" },
            date: { label: "DATE / TIME", col: "started_at", w: "130px" },
            direction: { label: "DIRECTION", col: null, w: "80px" },
            recording: { label: "", col: null, w: "40px" },
          };
          const cols = visibleCols.filter(id => colDefs[id]);
          const gridTemplate = "36px " + cols.map(id => colDefs[id].w).join(" ");

          return (<>
            {/* Header */}
            <div style={{ display: "grid", gridTemplateColumns: gridTemplate, padding: "10px 16px", borderBottom: "1px solid rgba(217,219,221,0.10)", background: "transparent", alignItems: "center" }}>
              <div><Checkbox checked={allSelected} onChange={toggleAll} /></div>
              {cols.map(id => { const h = colDefs[id]; return (
                <div key={id} onClick={() => h.col && handleSort(h.col)}
                  style={{ fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".08em", textTransform: "uppercase", cursor: h.col ? "pointer" : "default", userSelect: "none", display: "flex", alignItems: "center" }}>
                  {h.label}{h.col && <SortIcon col={h.col} />}
                </div>
              ); })}
            </div>

            {/* Rows */}
            {filtered.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: T.tx3, fontSize: 12 }}>
                {searchTerm ? "No calls match your search." : filters.length > 0 ? "No calls match these filters." : "No call logs yet. Make your first call from the Dialer!"}
              </div>
            ) : (
              filtered.map(log => {
                // Joined-row reads. Foreign-table embed delivers contact /
                // disposition / user inline; no in-memory enrichment.
                const c = log.contacts;
                const u = log.users;
                const phoneNumber = c?.phone || log.notes || "";
                const st = statusConfig[log.status] || statusConfig.no_answer;
                const cellMap = {
                  contact: (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {c ? (
                        <div
                          onClick={() => {
                            // ContactRecord opens via the People page event bridge.
                            // Reshape the joined contact row into the legacy local
                            // shape (fn/ln/co) that ContactRecord expects.
                            const ctLocal = { id: c.id, fn: c.first_name, ln: c.last_name, co: c.company, ph: c.phone };
                            window.history.pushState({}, "", "/contacts");
                            setTimeout(() => { window.__coreflow_open_contact = ctLocal; window.dispatchEvent(new Event("coreflow_open_contact")); }, 300);
                          }}
                          style={{ cursor: "pointer" }}>
                          <div style={{ fontSize: 12, fontWeight: 400, color: "#FAFAFA" }}>{(c.first_name || "") + " " + (c.last_name || "")}</div>
                          <div style={{ fontSize: 10, color: T.tx3 }}>{c.company || "—"}</div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>Unknown Contact</div>
                          <div style={{ fontSize: 10, color: T.tx3 }}>Not in CRM</div>
                        </div>
                      )}
                    </div>
                  ),
                  phone: <div style={{ fontSize: 11, color: T.txt, fontFamily: "inherit" }}>{formatPhone(phoneNumber)}</div>,
                  assigned: (() => {
                    if (!u) return <div><span style={{ fontSize: 10, color: T.tx3 }}>—</span></div>;
                    const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
                    return <div><span style={{ fontSize: 10, color: name ? T.tx2 : T.tx3 }}>{name || "—"}</span></div>;
                  })(),
                  status: <div><span style={{ fontSize: 10, fontWeight: 400, color: st.color }}>{st.label}</span></div>,
                  disposition: (() => {
                    const disp = log.call_dispositions;
                    if (!disp) return <span style={{ fontSize: 11, color: T.tx3 }}>—</span>;
                    return (
                      <span style={{
                        padding: "3px 8px",
                        borderRadius: 6,
                        border: "1px solid rgba(217,219,221,0.12)",
                        background: "rgba(255,255,255,0.06)",
                        color: T.tx2,
                        fontSize: 10,
                        fontWeight: 500,
                        display: "inline-block",
                      }}>{disp.label}</span>
                    );
                  })(),
                  duration: <div style={{ fontSize: 12, fontWeight: 400, color: log.duration > 0 ? T.txt : T.tx3, fontFamily: "inherit" }}>{fmt(log.duration || 0)}</div>,
                  date: <div style={{ fontSize: 10, color: T.tx2 }}>{formatDate(log.started_at)}</div>,
                  direction: <div><span style={{ fontSize: 10, color: "#FAFAFA", textTransform: "capitalize" }}>{log.direction || "Outbound"}</span></div>,
                  recording: <div>{log.recording_url ? <RecPlayBtn url={log.recording_url} /> : <span style={{ fontSize: 9, color: T.tx3 }}>—</span>}</div>,
                };
                return (
                  <div key={log.id}>
                    <div style={{
                      display: "grid", gridTemplateColumns: gridTemplate,
                      padding: "10px 16px", borderBottom: expandedLogId === log.id ? "none" : "1px solid rgba(217,219,221,0.06)",
                      alignItems: "center", transition: "background 0.1s", cursor: "pointer",
                      background: expandedLogId === log.id ? "rgba(64,89,173,0.06)" : "transparent",
                    }}
                      onClick={() => {
                        const next = expandedLogId === log.id ? null : log.id;
                        setExpandedLogId(next);
                        if (next && !analysisCache[log.id]) {
                          apiFetch("/api/ai/notetaker", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "get_analysis", callLogId: log.id }) })
                            .then(r => r.json()).then(d => { if (d.analysis) setAnalysisCache(p => ({ ...p, [log.id]: d.analysis })); else setAnalysisCache(p => ({ ...p, [log.id]: "none" })); })
                            .catch(() => setAnalysisCache(p => ({ ...p, [log.id]: "none" })));
                        }
                      }}
                      onMouseEnter={e => { if (expandedLogId !== log.id) e.currentTarget.style.background = T.bg; }}
                      onMouseLeave={e => { if (expandedLogId !== log.id) e.currentTarget.style.background = "transparent"; }}>
                      <div onClick={e => e.stopPropagation()}><Checkbox checked={selected.has(log.id)} onChange={() => toggleOne(log.id)} /></div>
                      {cols.map(id => <div key={id}>{cellMap[id]}</div>)}
                    </div>
                    {expandedLogId === log.id && (() => {
                      const a = analysisCache[log.id];
                      if (!a) return <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(217,219,221,0.06)", fontSize: 11, color: T.tx3 }}>Loading analysis...</div>;
                      if (a === "none") return (<div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(217,219,221,0.06)", display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 11, color: T.tx3 }}>{log.recording_url ? "No analysis yet." : "No recording available."}</span>
                        {log.recording_url && <button onClick={e => { e.stopPropagation(); apiFetch("/api/ai/notetaker", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "process_full", callLogId: log.id }) }).then(r => r.json()).then(d => { if (d.analysisId) setAnalysisCache(p => ({ ...p, [log.id]: { status: "pending" } })); }); }} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", cursor: "pointer", fontFamily: "inherit" }}>Analyze Call</button>}
                      </div>);
                      if (a.status && a.status !== "completed") return <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(217,219,221,0.06)", fontSize: 11, color: "#6B83D4" }}>Processing: {a.status}...</div>;
                      return (<div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(217,219,221,0.06)", background: "rgba(255,255,255,0.02)" }}>
                        {a.summary?.headline && <div style={{ fontSize: 12, color: "#FAFAFA", marginBottom: 6 }}>{a.summary.headline}</div>}
                        {a.summary?.key_points?.map((p, i) => <div key={i} style={{ fontSize: 11, color: T.tx2, lineHeight: 1.6, marginBottom: 1 }}>• {p}</div>)}
                        {a.flagged_moments?.length > 0 && <div style={{ marginTop: 8 }}>{a.flagged_moments.slice(0, 3).map((m, i) => <div key={i} style={{ fontSize: 10, color: T.tx3, marginBottom: 2 }}><span style={{ color: m.type === "objection" ? "#E53935" : m.type === "commitment" ? "#2E8B57" : "#D4763A", fontWeight: 500, textTransform: "uppercase", marginRight: 4, fontSize: 9 }}>{m.type}</span>"{m.text}"</div>)}</div>}
                        {a.sentiment?.overall && <div style={{ fontSize: 10, color: T.tx3, marginTop: 6 }}>Sentiment: <span style={{ color: a.sentiment.overall === "positive" ? "#2E8B57" : a.sentiment.overall === "negative" ? "#E53935" : T.tx3 }}>{a.sentiment.overall}</span></div>}
                        <div style={{ marginTop: 8 }}><button onClick={e => { e.stopPropagation(); const c = log.contacts; setCoachingPopup({ analysisId: a.id, contactName: c ? `${c.first_name || ""} ${c.last_name || ""}`.trim() : "Unknown", date: log.started_at, duration: log.duration }); }} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(107,131,212,0.1)", color: "#6B83D4", cursor: "pointer", fontFamily: "inherit" }}>Coaching</button></div>
                      </div>);
                    })()}
                  </div>
                );
              })
            )}
          </>);
        })()}

        {/* Pagination — driven by server-side totalCount, not the page slice */}
        {totalCount > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", flexShrink: 0, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 11, color: T.tx3 }}>{Math.min((currentPage - 1) * pageSize + 1, totalCount)}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount.toLocaleString()}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                {[20, 50, 100].map(size => (
                  <button key={size} onClick={() => { setPageSize(size); setCurrentPage(1); }} style={{ padding: "2px 6px", borderRadius: 4, fontSize: 10, border: "none", background: pageSize === size ? "rgba(64,89,173,0.15)" : "transparent", color: pageSize === size ? "#FAFAFA" : T.tx3, cursor: "pointer", fontFamily: "inherit" }}>{size}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "transparent", color: currentPage === 1 ? "rgba(250,250,250,0.15)" : "#FAFAFA", cursor: currentPage === 1 ? "default" : "pointer", fontFamily: "inherit", fontSize: 12 }}>‹</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => { let pn; if (totalPages <= 5) pn = i + 1; else if (currentPage <= 3) pn = i + 1; else if (currentPage >= totalPages - 2) pn = totalPages - 4 + i; else pn = currentPage - 2 + i; return (
                <button key={pn} onClick={() => setCurrentPage(pn)} style={{ width: 28, height: 28, borderRadius: 6, fontSize: 10, border: "none", fontFamily: "inherit", background: currentPage === pn ? "rgba(64,89,173,0.15)" : "transparent", color: currentPage === pn ? "#FAFAFA" : T.tx3, cursor: "pointer" }}>{pn}</button>
              ); })}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "transparent", color: currentPage === totalPages ? "rgba(250,250,250,0.15)" : "#FAFAFA", cursor: currentPage === totalPages ? "default" : "pointer", fontFamily: "inherit", fontSize: 12 }}>›</button>
            </div>
          </div>
        )}
      </div>
      </div>
      {coachingPopup && <CoachingPopup {...coachingPopup} onClose={() => setCoachingPopup(null)} />}
    </div>
  );
}
